import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { CreatePhysicianDto, PhysicianDto, SearchPhysicianDto } from '../lib/ServerApi';
import { api } from '../lib/api';

interface PhysicianSearchProps {
    onAddSuccess?: (e: string) => void;
    onAddFailed?: () => void;
    pageSize: number;
}

export const usePhysician = ({ onAddSuccess, pageSize }: PhysicianSearchProps) => {
    const [searchCriteria, setSearchCriteria] = useState<null | SearchPhysicianDto>();
    const queryClient = useQueryClient();

    const { data, isFetching } = useQuery<unknown, Error, SearchPhysicianDto[]>({
        queryKey: ['physician', 'search'],
        queryFn: async () => {
            if (searchCriteria) {
                const response = await api.physician.physicianSearch(searchCriteria);
                return response.data || [];
            }
            return [];
        },
        enabled: Boolean(searchCriteria),
    });

    useEffect(() => {
        queryClient.invalidateQueries({ queryKey: ['physician', 'search'] });
    }, [searchCriteria, queryClient]);

    const { mutate } = useMutation<PhysicianDto, unknown, CreatePhysicianDto>({
        mutationFn: async (data) => {
            const response = await api.physician.create(data);
            return response.data;
        },
        onSuccess: ({ id }) => {
            setSearchCriteria(null);
            onAddSuccess?.(id);
        },
    });

    return {
        setSearchCriteria: (data: SearchPhysicianDto) => {
            if (!data) {
                return;
            }
            const doSearch = Object.keys(data).some((key) => !!data[key as keyof SearchPhysicianDto]);
            if (doSearch) {
                setSearchCriteria({ ...data, page: 1, limit: pageSize });
            }
        },
        mutate: (e: CreatePhysicianDto) => mutate(e),
        data,
        isFetching,
        clearCache: () => {
            setSearchCriteria(null);
            queryClient.removeQueries({ queryKey: ['physician', 'search'] });
        },
        hasNextPage: data?.length === pageSize,
        page: searchCriteria?.page || 1,
        gotoPage: (page: number) => {
            setSearchCriteria((prev) => {
                return { ...prev, page };
            });
        },
    };
};
